/**
 * Analyse mode types — Track C4
 *
 * Exact mirror of the React app's type definitions.
 * Do NOT rename fields — they map 1:1 to backend API responses.
 */

// ─── Upload ───────────────────────────────────────────────────────────────────

export interface UploadProgress {
  percent: number;
  phase: "generating_url" | "uploading" | "processing" | "complete";
  message: string;
}

export interface UploadResult {
  file_id: string;
  filename: string;
  row_count: number;
  column_count: number;
  columns: string[];
  detected_schema: any;
  duplicate: boolean;
  gcs_key?: string;
}

// ─── Job statuses ─────────────────────────────────────────────────────────────

/** Raw status strings the backend can return */
export type BackendJobStatus =
  | "PENDING"
  | "STARTED"
  | "PROGRESS"
  | "PROCESSING"
  | "SUCCESS"
  | "CACHED"
  | "FAILURE"
  | "QUEUED"
  | "queued"
  | "processing"
  | "completed"
  | "failed";

/** Frontend-normalised status */
export type JobStatus = "completed" | "processing" | "error";

export function normaliseJobStatus(raw: string): JobStatus {
  if (raw === "SUCCESS" || raw === "CACHED" || raw === "completed") return "completed";
  if (raw === "FAILURE" || raw === "failed") return "error";
  return "processing";
}

// ─── Job list ─────────────────────────────────────────────────────────────────

export interface AnalysisJob {
  id: string;
  title: string;           // job_title from backend
  type: string;            // analysis_type from backend
  status: JobStatus;
  createdAt: string;       // formatted locale string
  timestamp: number;       // ms for sort
  results: BackendAnalysisResponse | null;
  summary?: Record<string, any>;
  progress?: number;
  progressMessage?: string;
}

// ─── Polling ──────────────────────────────────────────────────────────────────

export interface JobStatusResponse {
  job_id: string;
  status: BackendJobStatus;
  progress?: number;
  current_phase?: string;
  error?: string;
  result?: BackendAnalysisResponse & { is_partial?: boolean };
}

// ─── Analysis result shapes ───────────────────────────────────────────────────

export interface DomainInsight {
  insight: string;
  severity: "critical" | "warning" | "success" | "info";
  metric: string;
  business_impact: string;
  root_cause: string;
  recommendation: string;
  priority: "high" | "medium" | "low";
  effort: "high" | "medium" | "low";
  timeline: "immediate" | "short-term" | "long-term";
  tool_source?: string;   // older agents
  kpi_source?: string;    // LangGraph agents
  confidence: number;
  domain: string;
}

export interface PlotlySpec {
  data: any[];
  layout: any;
  [key: string]: any;
}

export interface BackendChart {
  id?: string;
  type: string;
  title: string;
  priority?: "critical" | "high" | "medium" | "low";
  question?: string;
  insights?: string;
  spec?: PlotlySpec;
}

export type BackendCharts = BackendChart[] | { [key: string]: BackendChart };

export interface MetricValue {
  value: number;
  formatted: string;
  title: string;
  explanation: string;
}

export interface StructuredInsights {
  overview: any[];
  risks: any[];
  recommendations: any[];
  generated_at?: string;
}

export interface BackendNarrative {
  headline?: string;
  summary: string;
  items?: string[];
  insights?: Array<{
    statement: string;
    severity: string;
    confidence: number;
    type: string;
  }>;
  generated_at?: string;
  structured_insights?: StructuredInsights;
}

/**
 * The full analysis response shape.
 * Backend can return either the new `payload`-based structure or the legacy
 * `analysis.*` structure. Both exist in production — handle both everywhere.
 */
export interface BackendAnalysisResponse {
  // New structure
  success?: boolean;
  file_id?: string;
  analysis_id?: string;
  execution_time_seconds?: number;
  analyses_performed?: string[];
  domains_analyzed?: number;
  primary_domain?: string;
  dataset_summary?: string;
  overall_confidence?: number;
  executive_summary?: string;

  payload?: {
    [domain: string]: {
      metrics?: any;
      domain_insights?: DomainInsight[];
      charts?: BackendChart[];
    } | any;
  } & {
    sensei_insights?: any;
    cross_domain_insights?: any[];
    executive_summary?: string;
    strategic_recommendations?: any[];
  };

  metrics?: any;
  insights?: {
    domain_insights?: DomainInsight[];
    cross_domain_insights?: any[];
    total_insights?: number;
    executive_summary?: string;
    strategic_recommendations?: any[];
  } | BackendNarrative | DomainInsight[];

  summary?: string;
  agent_type?: string;
  kpi_details?: any;
  sensei_insights?: any;
  charts?: BackendCharts;
  column_mapping?: any;
  schema?: any;
  file_info?: any;

  // Legacy structure
  mode?: string;
  file_count?: number;
  narrative?: BackendNarrative;
  structured_insights?: StructuredInsights;
  chart_data?: BackendCharts;
  signals?: { [key: string]: any };
  processing_time_ms?: number;
  analysis?: {
    metrics: any;
    narrative: BackendNarrative;
    insights: BackendNarrative;
    structured_insights?: StructuredInsights;
    charts: BackendCharts;
  };

  // Partial result flag
  is_partial?: boolean;

  // Dynamic domain keys (e.g. logistics_langgraph, procurement_langgraph)
  [key: string]: any;
}

// ─── Analysis state ───────────────────────────────────────────────────────────

export interface AnalysisState {
  isProcessing: boolean;
  isUploading: boolean;
  progress: number;
  progressMessage: string;
  partialResults: (BackendAnalysisResponse & { is_partial?: boolean }) | null;
  results: BackendAnalysisResponse | null;
  error: string | null;
}

export const initialAnalysisState: AnalysisState = {
  isProcessing: false,
  isUploading: false,
  progress: 0,
  progressMessage: "",
  partialResults: null,
  results: null,
  error: null,
};

// ─── Analysis type options ────────────────────────────────────────────────────

export interface AnalysisOption {
  id: string;
  title: string;
  description: string;
}

export const ANALYSIS_OPTIONS: AnalysisOption[] = [
  {
    id: "any",
    title: "Analyse Any Supply Chain Data",
    description:
      "Upload any CSV or Excel file for AI-powered analysis, insights, and expert recommendations.",
  },
  {
    id: "spend",
    title: "Spend Analytics",
    description:
      "Upload your spend data files (CSV, XLSX) for comprehensive spend analysis and expert recommendation powered by AI.",
  },
  {
    id: "inventory",
    title: "Inventory Health Check",
    description:
      "Upload your inventory data files (CSV, XLSX) for inventory health check analysis and expert recommendations powered by AI.",
  },
  {
    id: "logistics",
    title: "Logistic Analysis",
    description:
      "Upload your logistics data files (CSV, XLSX) for freight cost, delivery performance, route optimization analysis, and expert recommendations.",
  },
];
